import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LOCATIONS } from "@/data/siteData";

// Brand-coloured teardrop pin, so we don't depend on Leaflet's bundled marker images.
const pinIcon = L.divIcon({
  className: "coffice-map-pin",
  html: `<svg viewBox="0 0 32 40" width="32" height="40" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 11 16 24 16 24s16-13 16-24c0-8.837-7.163-16-16-16z" fill="#f97316"/>
      <circle cx="16" cy="16" r="6" fill="#ffffff"/>
    </svg>`,
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -36],
});

// Built as real DOM rather than JSX because Leaflet popups live outside the React tree.
// The Tailwind classes below must stay as literal strings here so the JIT scanner sees them.
const buildPopup = (loc, onViewDetails) => {
  const el = document.createElement("div");
  el.className = "font-body min-w-[13rem] max-w-[16rem]";

  const name = document.createElement("p");
  name.className = "font-heading text-base font-semibold text-slate-900";
  name.textContent = loc.name;

  const tagline = document.createElement("p");
  tagline.className = "text-[11px] uppercase tracking-[0.15em] font-medium text-orange-500 mb-2";
  tagline.textContent = loc.tagline;

  const address = document.createElement("p");
  address.className = "text-xs text-slate-600 leading-relaxed mb-3";
  address.textContent = loc.address;

  const actions = document.createElement("div");
  actions.className = "flex items-center gap-3";

  const directions = document.createElement("a");
  // Route to the pinned point, not the address string — some addresses are still placeholders.
  directions.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    loc.coords ? `${loc.coords.lat},${loc.coords.lng}` : `${loc.name}, ${loc.address}`
  )}`;
  directions.target = "_blank";
  directions.rel = "noopener noreferrer";
  directions.textContent = "Directions";

  directions.className =
    "text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors";

  const details = document.createElement("a");
  details.href = `/locations/${loc.id}`;
  details.textContent = "View Details";
  details.className =
    "inline-block rounded-full bg-orange-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-600 transition-colors";
  details.addEventListener("click", (e) => {
    // Keep navigation inside the SPA instead of triggering a full page load.
    e.preventDefault();
    onViewDetails(`/locations/${loc.id}`);
  });

  actions.append(details, directions);
  el.append(name, tagline, address, actions);
  return el;
};

export const LocationsMap = ({ className = "" }) => {
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  // Kept in a ref so a changing navigate identity never tears down and rebuilds the map.
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    const pins = LOCATIONS.filter((loc) => loc.coords);
    if (!containerRef.current || pins.length === 0) return undefined;

    const map = L.map(containerRef.current, {
      // Don't hijack the page scroll — users zoom with the +/- buttons or ctrl+wheel.
      scrollWheelZoom: false,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    pins.forEach((loc) => {
      L.marker([loc.coords.lat, loc.coords.lng], {
        icon: pinIcon,
        title: `${loc.name} — Coffice 24x7`,
        alt: `${loc.name} — Coffice 24x7`,
        riseOnHover: true,
      })
        .addTo(map)
        .bindPopup(buildPopup(loc, (path) => navigateRef.current(path)), {
          closeButton: true,
          minWidth: 200,
        });
    });

    map.fitBounds(
      L.latLngBounds(pins.map((loc) => [loc.coords.lat, loc.coords.lng])),
      { padding: [56, 56], maxZoom: 13 }
    );

    return () => map.remove();
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="locations-map"
      className={className}
      role="region"
      aria-label="Map of Coffice 24x7 coworking locations in Pune"
    />
  );
};
